import { useTranslation } from "react-i18next";
import { Card } from "../../../components/data/Card";
import { type KeyValueItem, KeyValueList } from "../../../components/data/KeyValueList";
import { Credits } from "../../../components/format/Credits";
import { Duration } from "../../../components/format/Duration";
import { Money } from "../../../components/format/Money";
import { Num } from "../../../components/format/Num";
import { useBookContext } from "../bookContext";

/**
 * What the Book has cost and what is left to spend: the Estimate for the
 * Chapters still to render, what the render has spent so far, and how much
 * audio exists. The Estimate here is the same figure the render screen gates
 * on; this card only shows it.
 *
 * The rows are built outside the JSX because their keys are identifiers, not
 * words, and the literal-string lint reads every literal inside a prop as text.
 */
export function SpendCard() {
  const { t } = useTranslation("overview");
  const { book } = useBookContext();
  const audioMs = book.chapters.reduce(
    (total, chapter) => total + (chapter.audio?.durationMs ?? 0),
    0,
  );
  const estimate = book.estimate;
  // Four different silences, and only one of them is good news: an Estimate
  // with Chapters in it, everything already rendered, scripts that nobody has
  // approved yet (the Estimate counts Approved Chapters only), or no script at
  // all. Saying "no script" while the table shows characters and a cost for
  // every Chapter is the case this is here to stop.
  const included = book.chapters.filter((chapter) => !chapter.excluded);
  const everythingRendered =
    included.length > 0 && included.every((chapter) => chapter.audio && !chapter.audio.stale);
  const hasScript = included.some((chapter) => chapter.versionCount > 0);
  const remaining: KeyValueItem[] =
    estimate && estimate.chapterCount > 0
      ? [
          {
            key: "money",
            label: t("spend.estimateRemaining"),
            value: <Money cents={estimate.money.cents} approx />,
            tone: "accent",
          },
          {
            key: "chapters",
            label: t("spend.estimateChapters"),
            value: <Num value={estimate.chapterCount} />,
          },
          {
            key: "characters",
            label: t("spend.characters"),
            value: <Num value={estimate.characters} />,
          },
          {
            key: "credits",
            label: t("spend.credits"),
            value: <Credits value={estimate.credits} />,
          },
        ]
      : [];
  const done: KeyValueItem[] = [];
  if (book.render) {
    done.push({
      key: "spent",
      label: t("spend.spent"),
      value: <Money cents={book.render.spent.cents} />,
    });
  }
  if (audioMs > 0) {
    done.push({
      key: "duration",
      label: t("spend.audioDuration"),
      value: <Duration ms={audioMs} style="words" />,
    });
  }

  return (
    <Card label={t("spend.label")}>
      {remaining.length > 0 ? (
        <KeyValueList items={remaining} />
      ) : everythingRendered ? (
        <p className="text-ink-2">{t("spend.nothingLeft")}</p>
      ) : hasScript ? (
        <p className="text-ink-2">{t("spend.nothingApproved")}</p>
      ) : (
        <p className="text-ink-2">{t("spend.noEstimateYet")}</p>
      )}
      {done.length > 0 ? (
        <KeyValueList className="mt-3 border-t border-line-soft pt-3" items={done} />
      ) : null}
    </Card>
  );
}
